import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Container } from '@/components/layout/Container';
import { Stack } from '@/components/layout/Stack';
import { Card, CardContent } from '@/components/ui/Card';
import { LoadingState } from '@/components/ui/Loading';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import { AspirationForm } from '@/components/aspirations/AspirationForm';
import { AspirationsList } from '@/components/aspirations/AspirationsList';
import { AspirationFiltersComponent } from '@/components/aspirations/AspirationFilters';
import { DeleteConfirmModal } from '@/components/shared/DeleteConfirmModal';
import { useAspirationsStore } from '@/store/aspirationsStore';
import { useGoalsStore } from '@/store/goalsStore';
import { useListsStore } from '@/store/listsStore';
import { useNavigationStore } from '@/store/navigationStore';
import { Aspiration, AspirationFilters, AspirationSort } from '@/types/aspirations';
import { Plus } from 'lucide-react';
import { useEntityPage, createSortFn } from '@/hooks/useEntityPage';

// Default filter and sort configurations
const defaultFilters: AspirationFilters = {};
const defaultSort: AspirationSort = { field: 'priority', direction: 'asc' };

// Filter function for aspirations
function filterAspirations(aspirations: Aspiration[], filters: AspirationFilters): Aspiration[] {
  let filtered = [...aspirations];

  // Apply search filter
  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filtered = filtered.filter(aspiration =>
      aspiration.title.toLowerCase().includes(searchLower) ||
      aspiration.description.toLowerCase().includes(searchLower)
    );
  }

  // Apply category filter
  if (filters.category?.length) {
    filtered = filtered.filter(aspiration =>
      filters.category!.includes(aspiration.category)
    );
  }

  // Apply status filter
  if (filters.status?.length) {
    filtered = filtered.filter(aspiration =>
      filters.status!.includes(aspiration.status)
    );
  }

  return filtered;
}

// Sort function using the helper
const sortAspirations = createSortFn<Aspiration>();

export function Aspirations() {
  const navigate = useNavigate();

  const {
    aspirations: rawAspirations,
    isLoading,
    error,
    initializeAspirations,
    deleteAspiration,
    reorderAspirations
  } = useAspirationsStore();

  const { goals: rawGoals, initializeGoals, getGoalsWithCalculatedProgress } = useGoalsStore();
  const { lists, initializeLists } = useListsStore();
  const { setPageInfo } = useNavigationStore();

  // Track re-render trigger for completion updates
  const [completionUpdateTrigger, setCompletionUpdateTrigger] = React.useState(0);

  // Use calculated progress from actual list item completions
  const goals = React.useMemo(() => {
    return getGoalsWithCalculatedProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawGoals, completionUpdateTrigger]);

  // Enrich aspirations with linked goals data
  const aspirations = React.useMemo(() => {
    return rawAspirations.map(aspiration => {
      const aspirationGoals = goals.filter(g => g.aspirationId === aspiration.id);
      return {
        ...aspiration,
        linkedGoals: aspirationGoals.map(g => ({
          id: g.id,
          title: g.title,
          description: g.description,
          completed: g.status === 'completed',
          deadline: g.deadline,
          completedAt: g.completedDate
        }))
      };
    });
  }, [rawAspirations, goals]);

  // Use the unified entity page hook
  const {
    filters,
    setFilters,
    sort,
    setSort,
    filteredItems: filteredAspirations,
    clearFilters,
    isFormOpen,
    setIsFormOpen,
    editingId: editingAspirationId,
    setEditingId: setEditingAspirationId,
    deleteConfirm,
    setDeleteConfirm,
    openCreateForm,
    closeForm,
  } = useEntityPage<Aspiration, AspirationFilters, AspirationSort>({
    items: aspirations,
    initializeItems: initializeAspirations,
    setPageInfo,
    pageTitle: 'Aspirations',
    actionLabel: 'New Aspiration',
    actionIcon: Plus,
    defaultFilters,
    defaultSort,
    filterFn: filterAspirations,
    sortFn: sortAspirations,
  });

  // Initialize goals and lists on mount if empty
  React.useEffect(() => {
    if (rawGoals.length === 0) {
      initializeGoals();
    }
    if (lists.length === 0) {
      initializeLists();
    }
  }, [rawGoals.length, lists.length, initializeGoals, initializeLists]);

  // Force re-render when completion data changes
  React.useEffect(() => {
    const handleCompletionUpdate = () => {
      setCompletionUpdateTrigger(prev => prev + 1);
    };
    window.addEventListener('universal-completion-updated', handleCompletionUpdate);
    return () => {
      window.removeEventListener('universal-completion-updated', handleCompletionUpdate);
    };
  }, []);

  // Calculate cascade info for delete confirmation
  const cascadeInfo = React.useMemo(() => {
    if (!deleteConfirm) return [];
    const aspirationGoals = goals.filter(g => g.aspirationId === deleteConfirm.id);
    const goalIds = aspirationGoals.map(g => g.id);
    const aspirationLists = lists.filter(l => l.goalId && goalIds.includes(l.goalId));
    const totalItems = aspirationLists.reduce((sum, list) => sum + list.items.length, 0);

    return [
      { label: 'linked goal', count: aspirationGoals.length },
      { label: 'linked list', count: aspirationLists.length },
      { label: 'list item', count: totalItems },
    ];
  }, [deleteConfirm, goals, lists]);

  // Delete handler
  const handleDeleteAspiration = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteAspiration(deleteConfirm.id);
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Failed to delete aspiration:', err);
    }
  };

  // Reorder handler
  const handleReorderAspirations = (reorderedAspirations: Aspiration[]) => {
    console.log('🚀 Starting reorder operation...');
    reorderAspirations(reorderedAspirations);
  };

  // Navigation handler
  const handleAspirationClick = (aspiration: Aspiration) => {
    navigate(`/goals?aspirationId=${aspiration.id}`);
  };

  // Edit handler
  const openEditForm = (aspiration: Aspiration) => {
    setEditingAspirationId(aspiration.id);
    setIsFormOpen(true);
  };

  // Filter change handler
  const handleFiltersChange = (newFilters: AspirationFilters) => {
    setFilters(newFilters);
  };

  if (isLoading) {
    return (
      <Container size="full" className="py-6">
        <LoadingState title="Loading aspirations..." />
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="full" className="py-6">
        <ErrorState
          title="Failed to load aspirations"
          description={error}
          action={{
            label: "Try Again",
            onClick: initializeAspirations
          }}
        />
      </Container>
    );
  }

  const hasAspirations = aspirations.length > 0;

  return (
    <Container size="full" className="py-6">
      <Stack spacing="lg">

        {hasAspirations ? (
          <Stack spacing="lg">
            <AspirationFiltersComponent
              filters={filters}
              sort={sort}
              onFiltersChange={handleFiltersChange}
              onSortChange={setSort}
              onClearFilters={clearFilters}
              totalCount={aspirations.length}
              filteredCount={filteredAspirations.length}
            />

            {filteredAspirations.length > 0 ? (
              <AspirationsList
                aspirations={filteredAspirations}
                onEdit={openEditForm}
                onDelete={setDeleteConfirm}
                onClick={handleAspirationClick}
                onReorder={handleReorderAspirations}
              />
            ) : (
              <Card>
                <CardContent className="py-12">
                  <EmptyState
                    title="No aspirations match your filters"
                    description="Try adjusting your search or filter criteria to see more results."
                    action={{
                      label: "Clear Filters",
                      onClick: clearFilters
                    }}
                  />
                </CardContent>
              </Card>
            )}
          </Stack>
        ) : (
          <Card>
            <CardContent className="py-12">
              <EmptyState
                title="No aspirations yet"
                description="Start by creating your first aspiration - a big dream or long-term goal you want to achieve."
                action={{
                  label: "Create Aspiration",
                  onClick: openCreateForm
                }}
              />
            </CardContent>
          </Card>
        )}
      </Stack>

      {/* Create/Edit Form Modal */}
      <AspirationForm
        isOpen={isFormOpen}
        onClose={closeForm}
        aspirationId={editingAspirationId || undefined}
        isEditing={!!editingAspirationId}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDeleteAspiration}
        title="Delete Aspiration"
        itemName={deleteConfirm?.title || ''}
        itemType="aspiration"
        description="All linked goals, lists, and items will be permanently deleted."
        cascadeInfo={cascadeInfo}
      />
    </Container>
  );
}